import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api';
import { Aviso, Campo, Carregando, ErroDaApi } from '../ui';

/**
 * Primeiro acesso — a tela onde a senha nasce.
 *
 * É pública porque quem chega aqui, por definição, ainda não consegue
 * entrar. O que a protege é o segredo de 256 bits no endereço, de uso
 * único e com prazo.
 *
 * Três decisões de desenho que parecem detalhe e não são:
 *
 * **A identificação aparece, e não pode ser editada.** A pessoa precisa
 * confirmar que o link é dela — link trocado entre dois testadores é o
 * erro mais provável de toda esta operação, e ele é silencioso: sem o
 * e-mail à vista, o beta define a senha da conta do colega e ninguém
 * descobre até alguém não conseguir entrar.
 *
 * **A força aparece enquanto digita, com a regra escrita.** "Senha
 * fraca" sem dizer o que falta obriga a pessoa a adivinhar, e ela
 * adivinha acrescentando `!` no fim. A regra em texto produz senha
 * melhor do que o medidor colorido.
 *
 * **Não há campo de confirmação escondido.** Em vez de digitar duas
 * vezes, existe "mostrar senha": a pessoa confere o que escreveu. Dois
 * campos mascarados detectam erro de digitação; um campo visível
 * previne. Numa bancada de laboratório, o risco de alguém ler a tela por
 * cima do ombro é menor que o de a pessoa ficar trancada fora da conta.
 */

interface Conferencia {
  vale: boolean;
  /*
   * A identificação com que a pessoa vai entrar. A API manda
   * `coalesce(email, login)` desde a 0052 — o campo continua se
   * chamando `email` no contrato para não quebrar cliente parado numa
   * versão antiga, mas o CONTEÚDO passou a ser a identidade.
   */
  email?: string;
  nome?: string | null;
  expira_em?: string;
  primeiro_acesso?: boolean;
  minimo?: number;
}

/** As mesmas exigências do servidor, escritas para a pessoa ler. */
function exigencias(senha: string, identificacao: string, minimo: number) {
  const baixa = senha.toLowerCase();
  const local = identificacao.split('@')[0]?.toLowerCase() ?? '';
  return [
    {
      texto: `Pelo menos ${minimo} caracteres`,
      ok: senha.length >= minimo,
    },
    {
      texto: 'Pelo menos 5 caracteres diferentes',
      ok: new Set(baixa).size >= 5,
    },
    {
      texto: 'Não contém o seu nome de usuário',
      ok: !(local.length >= 4 && baixa.includes(local)),
    },
    {
      texto: 'Não é uma sequência do teclado nem uma senha óbvia',
      ok: !/0123456789|abcdefghij|qwertyuiop/.test(baixa)
        && !['senha', 'password', 'testesolo', '123456', 'qwerty', 'admin']
          .some((o) => baixa.includes(o)),
    },
  ];
}

export function PrimeiroAcesso() {
  const { token = '' } = useParams();
  const navegar = useNavigate();

  const [conf, setConf] = useState<Conferencia | null>(null);
  const [senha, setSenha] = useState('');
  const [mostrar, setMostrar] = useState(false);
  const [erro, setErro] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);
  const [pronto, setPronto] = useState(false);

  useEffect(() => {
    let vivo = true;
    api<Conferencia>(`/convite/${encodeURIComponent(token)}`)
      .then((c) => { if (vivo) setConf(c); })
      .catch(() => { if (vivo) setConf({ vale: false }); });
    return () => { vivo = false; };
  }, [token]);

  if (!conf) return <div className="entrada"><div className="entrada-caixa"><Carregando /></div></div>;

  if (!conf.vale) {
    return (
      <div className="entrada"><div className="entrada-caixa larga">
        <h1>Convite inválido</h1>
        <Aviso tipo="erro">
          Este link não vale mais. Convites são de uso único e vencem em
          72 horas — se você já definiu sua senha, é só entrar. Caso
          contrário, peça um convite novo a um administrador.
        </Aviso>
        <button type="button" className="botao largo"
          onClick={() => navegar('/entrar', { replace: true })}>
          Ir para a tela de entrada
        </button>
      </div></div>
    );
  }

  if (pronto) {
    return (
      <div className="entrada"><div className="entrada-caixa larga">
        <h1>Senha definida</h1>
        {/*
          * UX-002: o botão de cima dizia «Definir senha e entrar» e esta
          * tela mandava entrar de novo. Agora rótulo e comportamento
          * dizem a mesma coisa: definir a senha NÃO entra — a entrada é
          * o próximo passo, e o botão leva a ela.
          */}
        <Aviso tipo="ok">
          Sua conta está ativa. Na tela de entrada, use{' '}
          <strong>{conf.email}</strong> e a senha que você acabou de escolher.
        </Aviso>
        <button type="button" className="botao largo grande"
          onClick={() => navegar('/entrar', { replace: true })}>
          Ir para a entrada
        </button>
      </div></div>
    );
  }

  const minimo = conf.minimo ?? 12;
  const lista = exigencias(senha, conf.email ?? '', minimo);
  const podeEnviar = senha.length > 0 && lista.every((e) => e.ok) && !enviando;

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    setEnviando(true);
    try {
      await api(`/convite/${encodeURIComponent(token)}`, {
        metodo: 'POST', corpo: { senha },
      });
      setSenha('');
      setPronto(true);
    } catch (err) {
      setErro(err);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="entrada"><div className="entrada-caixa larga">
      <h1 style={{ marginBottom: '.3rem' }}>
        {conf.primeiro_acesso ? 'Primeiro acesso' : 'Nova senha'}
      </h1>
      <p className="sub" style={{ marginBottom: '1.2rem' }}>
        {conf.nome ? `${conf.nome} — ` : ''}
        <strong>{conf.email}</strong>
      </p>

      <form className="cartao" onSubmit={enviar} noValidate>
        <ErroDaApi erro={erro} />

        <p className="sub" style={{ marginTop: 0 }}>
          Escolha uma senha que só você conheça. Ninguém do laboratório —
          nem o administrador que lhe mandou este link — consegue vê-la.
          É isso que faz a sua assinatura valer nos laudos.
        </p>

        <Campo
          rotulo="Sua senha"
          type={mostrar ? 'text' : 'password'}
          value={senha}
          autoComplete="new-password"
          autoFocus
          required
          onChange={(ev) => setSenha(ev.target.value)}
        />

        <label className="linha-check">
          <input
            type="checkbox"
            checked={mostrar}
            onChange={(ev) => setMostrar(ev.target.checked)}
          />
          Mostrar senha
        </label>

        <ul className="exigencias">
          {lista.map((e) => (
            <li key={e.texto} className={e.ok ? 'ok' : undefined}>
              <span aria-hidden="true">{e.ok ? '✓' : '·'}</span>
              <span>{e.texto}</span>
              <span className="só-leitor">{e.ok ? '(atendido)' : '(pendente)'}</span>
            </li>
          ))}
        </ul>

        <button type="submit" className="botao largo grande" disabled={!podeEnviar}>
          {enviando ? 'Definindo…' : 'Definir senha'}
        </button>
      </form>

      <p className="sub" style={{ textAlign: 'center' }}>
        Este link é de uso único e vence em 72 horas.
      </p>
    </div></div>
  );
}
